import { ActionIcon, Menu, Slider, Switch } from '@mantine/core';
import { Minus, Plus, Settings } from 'lucide-react';
import { FC, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import useBreakpoints from '@/hooks/useBreakpoints.ts';

import { useCalendarStore } from '@/stores/calendarStore.ts';
import { useFilterStore } from '@/stores/filterStore.ts';

import { setCalendarWeekendVisible } from '@/utils/calendarUtils.ts';
import { isDevEnvironment } from '@/utils/envUtils.ts';

const CalendarSettings: FC = () => {
    const appointment_multiplier = useFilterStore((state) => state.appointment_multiplier);
    const [local_multiplier, setLocalMultiplier] = useState(appointment_multiplier);
    const { is_week_view_enabled, is_tooltip_enabled, is_color_bg_enabled, is_weekend_visible } = useCalendarStore(
        useShallow((state) => ({
            is_week_view_enabled: state.is_week_view_enabled,
            is_tooltip_enabled: state.is_tooltip_enabled,
            is_color_bg_enabled: state.is_color_bg_enabled,
            is_weekend_visible: state.is_weekend_visible,
        }))
    );
    const { is_xl } = useBreakpoints();

    const handleToggleTooltip = (): void => useCalendarStore.setState({ is_tooltip_enabled: !is_tooltip_enabled });
    const handleToggleColorBg = (): void => useCalendarStore.setState({ is_color_bg_enabled: !is_color_bg_enabled });
    const handleToggleWorkWeek = (): void => setCalendarWeekendVisible(!is_weekend_visible);

    const handleMultiplierChange = (value: number): void => {
        useFilterStore.setState({ appointment_multiplier: value });
    };

    const handleIncreaseMultiplier = (): void => {
        const new_value = Math.min(50, appointment_multiplier + 1);
        handleMultiplierChange(new_value);
    };

    const handleDecreaseMultiplier = (): void => {
        const new_value = Math.max(1, appointment_multiplier - 1);
        handleMultiplierChange(new_value);
    };

    return (
        <Menu>
            <Menu.Target>
                <ActionIcon
                    variant="outline"
                    size="icon"
                    className="h-10 w-10 rounded-full border-gray-300 hover:bg-gray-100"
                >
                    <Settings className="size-5 text-slate-500" />
                </ActionIcon>
            </Menu.Target>

            <Menu.Dropdown>
                <Menu.Label>Einstellungen</Menu.Label>
                {is_xl && (
                    <Menu.Item component="div" closeMenuOnClick={false} className="cursor-default">
                        <Switch
                            label="Tooltip"
                            checked={is_tooltip_enabled}
                            onChange={handleToggleTooltip}
                            labelPosition="left"
                            className="w-full justify-between"
                        />
                    </Menu.Item>
                )}
                <Menu.Item component="div" closeMenuOnClick={false} className="cursor-default">
                    <Switch
                        label="Farbe"
                        checked={is_color_bg_enabled}
                        onChange={handleToggleColorBg}
                        labelPosition="left"
                        className="w-full justify-between"
                    />
                </Menu.Item>
                {is_week_view_enabled && (
                    <Menu.Item component="div" closeMenuOnClick={false} className="cursor-default">
                        <Switch
                            label="Wochenende"
                            checked={!is_weekend_visible}
                            onChange={handleToggleWorkWeek}
                            labelPosition="left"
                            className="w-full justify-between"
                        />
                    </Menu.Item>
                )}
                {isDevEnvironment() && (
                    <>
                        <Menu.Divider />
                        <Menu.Label>Test-Multiplikator</Menu.Label>
                        <Menu.Item component="div" closeMenuOnClick={false} className="cursor-default px-3">
                            <div className="flex flex-col gap-2">
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-sm">Termine: {appointment_multiplier}x</span>
                                    <div className="flex gap-1">
                                        <ActionIcon
                                            size="sm"
                                            variant="subtle"
                                            onClick={handleDecreaseMultiplier}
                                            disabled={appointment_multiplier <= 1}
                                        >
                                            <Minus className="size-3" />
                                        </ActionIcon>
                                        <ActionIcon
                                            size="sm"
                                            variant="subtle"
                                            onClick={handleIncreaseMultiplier}
                                            disabled={appointment_multiplier >= 50}
                                        >
                                            <Plus className="size-3" />
                                        </ActionIcon>
                                    </div>
                                </div>
                                <div className="pb-4">
                                    <Slider
                                        value={local_multiplier}
                                        onChange={setLocalMultiplier}
                                        onChangeEnd={handleMultiplierChange}
                                        min={1}
                                        max={20}
                                        marks={[
                                            { value: 1, label: '1x' },
                                            { value: 10, label: '10x' },
                                            { value: 20, label: '20x' },
                                        ]}
                                        className="w-full"
                                    />
                                </div>
                            </div>
                        </Menu.Item>
                    </>
                )}
            </Menu.Dropdown>
        </Menu>
    );
};

export default CalendarSettings;
